import React, { useCallback, useEffect, useState } from 'react';
import { GitMerge, RefreshCw, Mail, Phone, Calendar, MapPin, Check, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

const CONF_STYLE = {
    hoch: 'bg-green-500/15 text-green-600 border-green-500/30',
    mittel: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
    niedrig: 'bg-muted text-muted-foreground border-border',
};

const fmtDate = (v) => {
    if (!v) return '–';
    const d = new Date(v);
    return isNaN(d) ? String(v) : d.toLocaleDateString('de-AT');
};

const Field = ({ icon: Icon, children, muted }) => (
    <div className={`flex items-center gap-1.5 text-xs ${muted ? 'text-muted-foreground/60 italic' : 'text-muted-foreground'}`}>
        <Icon className="w-3 h-3 shrink-0" />
        <span className="truncate">{children}</span>
    </div>
);

/**
 * Reiter "Dubletten": erkannte Gruppen, Datensaetze nebeneinander, Auswahl des
 * fuehrenden Eintrags und Zusammenfuehren. Die Dubletten werden archiviert, nicht geloescht.
 */
const SocialFaceDuplicates = ({ api, canMerge = false, onChanged, onCount }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [minConfidence, setMinConfidence] = useState('mittel');
    // pro Gruppe: { keepId, mergeIds: Set }
    const [selection, setSelection] = useState({});
    const [merging, setMerging] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/social-faces/duplicates', { params: { min_confidence: minConfidence } });
            setData(res.data);
            const sel = {};
            (res.data.groups || []).forEach(g => {
                sel[g.group_id] = {
                    keepId: g.suggested_keep_id,
                    mergeIds: new Set(g.faces.filter(f => f.id !== g.suggested_keep_id).map(f => f.id)),
                };
            });
            setSelection(sel);
            if (onCount) onCount(res.data.total_groups || 0);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Dubletten konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    }, [api, minConfidence, onCount]);

    useEffect(() => { load(); }, [load]);

    const setKeep = (group, faceId) => {
        setSelection(prev => ({
            ...prev,
            [group.group_id]: {
                keepId: faceId,
                mergeIds: new Set(group.faces.filter(f => f.id !== faceId).map(f => f.id)),
            },
        }));
    };

    const toggleMerge = (group, faceId) => {
        setSelection(prev => {
            const cur = prev[group.group_id];
            const next = new Set(cur.mergeIds);
            if (next.has(faceId)) next.delete(faceId); else next.add(faceId);
            return { ...prev, [group.group_id]: { ...cur, mergeIds: next } };
        });
    };

    const merge = async (group) => {
        const sel = selection[group.group_id];
        const mergeIds = Array.from(sel.mergeIds);
        if (mergeIds.length === 0) { toast.error('Keine Dublette ausgewählt'); return; }
        const keep = group.faces.find(f => f.id === sel.keepId);
        if (!window.confirm(
            `${mergeIds.length} Eintrag/Einträge in "${keep.first_name} ${keep.last_name}" zusammenführen?\n\n` +
            'Leere Felder werden aufgefüllt, Punkte addiert, Shootings und Rechnungen umgehängt. ' +
            'Die Dubletten werden archiviert, nicht gelöscht.'
        )) return;
        setMerging(group.group_id);
        try {
            const res = await api.post('/social-faces/merge', { keep_id: sel.keepId, merge_ids: mergeIds });
            const m = res.data.moved || {};
            toast.success(`Zusammengeführt: ${m.shootings || 0} Shootings, ${m.invoices || 0} Rechnungen umgehängt`);
            await load();
            if (onChanged) onChanged();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Zusammenführen fehlgeschlagen');
        } finally {
            setMerging(null);
        }
    };

    return (
        <div className="space-y-4" data-testid="social-face-duplicates">
            <div className="flex flex-wrap items-center gap-3">
                <div className="text-sm text-muted-foreground flex-1 min-w-[200px]">
                    {data ? (
                        <>
                            <strong className="text-foreground">{data.total_groups}</strong> Gruppen mit{' '}
                            <strong className="text-foreground">{data.total_duplicate_records}</strong> überzähligen Einträgen
                            {' '}· hoch {data.by_confidence?.hoch || 0} · mittel {data.by_confidence?.mittel || 0} · niedrig {data.by_confidence?.niedrig || 0}
                        </>
                    ) : 'Suche Dubletten...'}
                </div>
                <Select value={minConfidence} onValueChange={setMinConfidence}>
                    <SelectTrigger className="w-[190px]" data-testid="duplicates-confidence">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="hoch">Nur sichere (hoch)</SelectItem>
                        <SelectItem value="mittel">Ab mittel</SelectItem>
                        <SelectItem value="niedrig">Alle (auch niedrig)</SelectItem>
                    </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={load} disabled={loading} title="Neu laden">
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
            </div>

            {!loading && data && data.groups.length === 0 && (
                <Card>
                    <CardContent className="p-12 text-center">
                        <Check className="w-12 h-12 mx-auto mb-4 text-green-500" />
                        <h3 className="text-lg font-medium mb-1">Keine Dubletten gefunden</h3>
                        <p className="text-muted-foreground text-sm">Mit dieser Sicherheitsstufe gibt es keine doppelten Einträge.</p>
                    </CardContent>
                </Card>
            )}

            {(data?.groups || []).map(group => {
                const sel = selection[group.group_id] || { keepId: group.suggested_keep_id, mergeIds: new Set() };
                return (
                    <Card key={group.group_id} data-testid={`duplicate-group-${group.group_id}`}>
                        <CardContent className="p-4 space-y-3">
                            <div className="flex flex-wrap items-center gap-2">
                                <Badge variant="outline" className={CONF_STYLE[group.confidence]}>
                                    Sicherheit: {group.confidence}
                                </Badge>
                                {group.reasons.map(r => (
                                    <Badge key={r} variant="secondary" className="text-[11px]">{r}</Badge>
                                ))}
                                <span className="text-xs text-muted-foreground ml-auto">{group.size} Einträge</span>
                            </div>

                            {/* Datensaetze nebeneinander */}
                            <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(230px, 1fr))` }}>
                                {group.faces.map(face => {
                                    const isKeep = sel.keepId === face.id;
                                    const willMerge = sel.mergeIds.has(face.id);
                                    const refs = face.references || {};
                                    return (
                                        <div
                                            key={face.id}
                                            className={`rounded-lg border p-3 space-y-1.5 transition-colors ${
                                                isKeep ? 'border-primary bg-primary/5' : willMerge ? 'border-border' : 'border-dashed border-border opacity-60'
                                            }`}
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <p className="font-medium text-sm truncate">{face.first_name} {face.last_name}</p>
                                                    <p className="text-[11px] text-muted-foreground">
                                                        Status: {face.status || '–'} · {face.filled_fields} Felder
                                                        {face.points > 0 && <span className="text-red-400"> · {face.points} P</span>}
                                                    </p>
                                                </div>
                                                {face.id === group.suggested_keep_id && (
                                                    <Badge variant="outline" className="text-[10px] shrink-0">Vorschlag</Badge>
                                                )}
                                            </div>
                                            <Field icon={Mail} muted={face.email_is_placeholder}>
                                                {face.email_is_placeholder ? 'Platzhalter-Adresse' : face.email}
                                            </Field>
                                            <Field icon={Phone}>{face.phone || '–'}</Field>
                                            <Field icon={Calendar}>{face.birthday || '–'}</Field>
                                            <Field icon={MapPin}>{[face.postal_code, face.city].filter(Boolean).join(' ') || '–'}</Field>
                                            <p className="text-[11px] text-muted-foreground">
                                                Angelegt {fmtDate(face.created_at)} · {refs.shootings || 0} Shootings · {refs.invoices || 0} Rechnungen
                                            </p>
                                            {face.is_blacklisted && (
                                                <p className="text-[11px] text-red-400 flex items-center gap-1">
                                                    <AlertTriangle className="w-3 h-3" /> Blacklist
                                                </p>
                                            )}
                                            {canMerge && (
                                                <div className="flex gap-2 pt-1">
                                                    <Button
                                                        size="sm"
                                                        variant={isKeep ? 'default' : 'outline'}
                                                        className="h-7 text-xs flex-1"
                                                        onClick={() => setKeep(group, face.id)}
                                                        data-testid={`duplicate-keep-${face.id}`}
                                                    >
                                                        {isKeep ? 'Führender Eintrag' : 'Als führend wählen'}
                                                    </Button>
                                                    {!isKeep && (
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            className="h-7 text-xs"
                                                            onClick={() => toggleMerge(group, face.id)}
                                                        >
                                                            {willMerge ? 'Auslassen' : 'Einbeziehen'}
                                                        </Button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {canMerge && (
                                <div className="flex justify-end">
                                    <Button
                                        onClick={() => merge(group)}
                                        disabled={merging === group.group_id || sel.mergeIds.size === 0}
                                        data-testid={`duplicate-merge-${group.group_id}`}
                                    >
                                        <GitMerge className="w-4 h-4 mr-2" />
                                        {merging === group.group_id ? 'Wird zusammengeführt...' : `Zusammenführen (${sel.mergeIds.size})`}
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
};

export default SocialFaceDuplicates;
